import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Button } from "components/ui";
import styles from "./dialog.module.scss";
import { SearchSelectInput } from "./Input";
import UserIcon from "Assets/icon/icon-user-check.svg";
import { ReactComponent as ChevronIcon } from "Assets/icon/icon-chevron.svg";
import appInfo from "utility/app-info";
import { Member } from "apps/company/pages/Members/dataTypes";
import { FetchResult, useMutation } from "@apollo/client";
import { MUTATION_ADD_ADMIN } from "apps/company/api/graphQL/mutation";
import { CommonModalProps } from "./common";
import { logError } from "utility/Function";
import { useTranslation } from "lib/i18n/use-translation";

interface AddAdminDialogProps extends CommonModalProps {
  members: Pick<Member, "id" | "member" | "firstName" | "lastName" | "email">[];
  onAPIResponse?: (response: FetchResult) => void;
}

const AddAdminDialog = ({
  onCloseAction,
  members,
  isOpen,
  title,
  description,
  action,
  onAPIResponse,
}: AddAdminDialogProps) => {
  //HOOKS
  const { t } = useTranslation(["admin"]);
  const [addAdmin] = useMutation(MUTATION_ADD_ADMIN);

  useEffect(() => {
    const membersMap: { value: string; label: string }[] = members.map(
      (member: Pick<Member, "id" | "firstName" | "lastName" | "email">) => ({
        value: `${member.id}`,
        //If members don't have either a first or last name then show email
        label: `${
          member.firstName.trimStart() || member.lastName.trimStart()
            ? `${member.firstName} ${member.lastName}`
            : `${member.email}`
        } `,
      })
    );
    setSearchableMembers(membersMap);
  }, [members]);

  //STATE
  const [searchableMembers, setSearchableMembers] = useState<
    { value: string; label: string }[]
  >([]);

  const [selectedOption, setSelectedOption] = useState<{
    value: string;
    label: string;
  } | null>();
  const [errorText, setErrorText] = useState<string>("");

  //HANDLERS
  const toggle = () => {
    reset();
    onCloseAction();
  };

  const reset = () => {
    setErrorText("");
    setSelectedOption(null);
  };

  const handleAddAdmin = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedOption || !selectedOption.value) {
      setErrorText(t("admin:adminSettings.addAdmin.selectMemberError"));
      return;
    }
    addAdmin({
      variables: {
        appInfo,
        memberUserId: selectedOption.value,
      },
    })
      .then((response) => {
        if (response.data) {
          if (response.data) {
            toggle();
            onAPIResponse(response);
            reset();
          }
        } else if (response.errors) {
          setErrorText(response.errors[0].message);
        }
      })
      .catch((err) => {
        setErrorText(err?.message);
        logError("Error adding admin", err);
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      style={{ width: "100%", maxWidth: "440px" }}
      className={styles["modal"]}
      contentClassName={styles["modal-content"]}
      centered
    >
      <form onSubmit={handleAddAdmin}>
        <ModalHeader className={styles["header"]} toggle={toggle}></ModalHeader>
        <ModalBody className={`${styles["body"]} ${styles.okta}`}>
          <p className={styles["title"]}>{title}</p>
          <p className={`text-small ${styles["description"]}`}>{description}</p>
          <div className={styles["inputs"]}>
            <SearchSelectInput
              required={true}
              option={selectedOption}
              icon={UserIcon}
              icon2={<ChevronIcon className={styles["down-arrow"]} />}
              placeholder={t("admin:adminSettings.addAdmin.searchPlaceholder")}
              data={searchableMembers}
              onAutocompleteChange={setSelectedOption}
            />
          </div>
          <p className={`sans ${styles["error"]}`}>{errorText}</p>
        </ModalBody>
        <ModalFooter className={styles["footer"]}>
          <Button variant="primary" className="sans" type="submit">
            {action}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default AddAdminDialog;
